/* Real-time monitor: WebSocket subscription to /api/ws/active-calls */
import React, { useEffect, useRef, useState } from 'react';
import { WaveSine, Phone, Clock, CheckCircle, XCircle, SpinnerGap, ArrowsClockwise } from '@phosphor-icons/react';

const Pill = ({ status }) => {
    const map = {
        in_progress: { bg: 'rgba(34,197,94,0.15)', c: '#22c55e', label: 'AKTIVNÍ' },
        answered:   { bg: 'rgba(34,197,94,0.15)', c: '#22c55e', label: 'PŘIJATO' },
        missed:     { bg: 'rgba(239,68,68,0.15)', c: '#ef4444', label: 'ZMEŠKÁNO' },
        failed:     { bg: 'rgba(239,68,68,0.15)', c: '#ef4444', label: 'CHYBA' },
        busy:       { bg: 'rgba(245,158,11,0.15)', c: '#f59e0b', label: 'OBSAZENO' },
    };
    const m = map[status] || { bg: 'rgba(148,163,184,0.15)', c: '#94a3b8', label: status?.toUpperCase() || '—' };
    return (
        <span className="px-2 py-0.5 rounded text-[10px] font-bold tracking-wider"
              style={{ background: m.bg, color: m.c }}>
            {m.label}
        </span>
    );
};

const RealtimeMonitor = () => {
    const [snapshot, setSnapshot] = useState(null);
    const [status, setStatus] = useState('connecting');
    const wsRef = useRef(null);

    const connect = React.useCallback(() => {
        const token = localStorage.getItem('vg_token');
        if (!token) { setStatus('no_token'); return; }
        const backend = process.env.REACT_APP_BACKEND_URL || '';
        const proto = backend.startsWith('https') ? 'wss' : 'ws';
        const host = backend.replace(/^https?:\/\//, '');
        const url = `${proto}://${host}/api/ws/active-calls?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;
        setStatus('connecting');
        ws.onopen = () => setStatus('connected');
        ws.onmessage = (e) => {
            try { setSnapshot(JSON.parse(e.data)); } catch (_) {}
        };
        ws.onclose = () => {
            setStatus('disconnected');
            setTimeout(() => { if (wsRef.current === ws) connect(); }, 3000);
        };
        ws.onerror = () => setStatus('error');
    }, []);

    useEffect(() => {
        connect();
        return () => { try { wsRef.current?.close(); } catch (_) {} };
    }, [connect]);

    const fmtDur = (s) => {
        const n = parseInt(s || 0, 10);
        const m = Math.floor(n / 60).toString().padStart(2, '0');
        const sc = (n % 60).toString().padStart(2, '0');
        return `${m}:${sc}`;
    };
    const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('cs-CZ') : '—';

    return (
        <div data-testid="realtime-monitor">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                        <WaveSine size={28} style={{ color: 'var(--brand)' }} />
                        Real-time Monitor
                    </h1>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                        Živé sledování aktivních hovorů přes WebSocket
                    </p>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider"
                     data-testid="rt-ws-status">
                    {status === 'connected' && (
                        <><CheckCircle size={16} weight="fill" color="#22c55e" /><span style={{ color: '#22c55e' }}>Připojeno</span></>
                    )}
                    {status === 'connecting' && (
                        <><SpinnerGap size={16} className="animate-spin" /><span>Připojuji…</span></>
                    )}
                    {(status === 'disconnected' || status === 'error') && (
                        <><XCircle size={16} weight="fill" color="#ef4444" />
                          <span style={{ color: '#ef4444' }}>Odpojeno</span>
                          <button onClick={connect} className="ml-2 px-2 py-1 rounded border"
                                  data-testid="rt-reconnect-btn">
                              <ArrowsClockwise size={12} className="inline mr-1" /> Reconnect
                          </button></>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="rounded-xl p-5 border"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] uppercase tracking-[0.2em]"
                              style={{ color: 'var(--text-secondary)' }}>Aktivní hovory</span>
                        <Phone size={16} style={{ color: 'var(--brand)' }} />
                    </div>
                    <div className="text-4xl font-bold font-mono" data-testid="rt-active-count">
                        {snapshot?.active_count ?? '—'}
                    </div>
                </div>
                <div className="rounded-xl p-5 border"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] uppercase tracking-[0.2em]"
                              style={{ color: 'var(--text-secondary)' }}>Poslední aktualizace</span>
                        <Clock size={16} style={{ color: 'var(--brand)' }} />
                    </div>
                    <div className="text-lg font-mono" data-testid="rt-last-update">
                        {fmtTime(snapshot?.timestamp)}
                    </div>
                </div>
                <div className="rounded-xl p-5 border"
                     style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] uppercase tracking-[0.2em]"
                              style={{ color: 'var(--text-secondary)' }}>Posledních 10</span>
                        <Phone size={16} style={{ color: 'var(--brand)' }} />
                    </div>
                    <div className="text-4xl font-bold font-mono" data-testid="rt-recent-count">
                        {snapshot?.recent?.length ?? 0}
                    </div>
                </div>
            </div>

            <div className="rounded-xl border overflow-hidden mb-6"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="px-5 py-3 border-b text-xs font-bold uppercase tracking-[0.15em]"
                     style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                    Aktivní hovory
                </div>
                <table className="w-full text-sm" data-testid="rt-active-table">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider"
                            style={{ color: 'var(--text-disabled)' }}>
                            <th className="px-5 py-2">Začátek</th>
                            <th className="px-5 py-2">Caller ID</th>
                            <th className="px-5 py-2">DID</th>
                            <th className="px-5 py-2">Cíl</th>
                            <th className="px-5 py-2">Stav</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(snapshot?.active || []).length === 0 ? (
                            <tr><td colSpan={5} className="px-5 py-8 text-center text-sm"
                                    style={{ color: 'var(--text-disabled)' }}>
                                Žádné aktivní hovory
                            </td></tr>
                        ) : (snapshot.active || []).map((c) => (
                            <tr key={c.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                <td className="px-5 py-3 font-mono text-xs">{fmtTime(c.started_at)}</td>
                                <td className="px-5 py-3 font-mono">{c.caller_id || '—'}</td>
                                <td className="px-5 py-3 font-mono">{c.did_number || '—'}</td>
                                <td className="px-5 py-3 font-mono">{c.destination || '—'}</td>
                                <td className="px-5 py-3"><Pill status={c.status} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="rounded-xl border overflow-hidden"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <div className="px-5 py-3 border-b text-xs font-bold uppercase tracking-[0.15em]"
                     style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                    Posledních 10 ukončených
                </div>
                <table className="w-full text-sm" data-testid="rt-recent-table">
                    <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider"
                            style={{ color: 'var(--text-disabled)' }}>
                            <th className="px-5 py-2">Čas</th>
                            <th className="px-5 py-2">Caller ID</th>
                            <th className="px-5 py-2">DID</th>
                            <th className="px-5 py-2">Trvání</th>
                            <th className="px-5 py-2">AI Intent</th>
                            <th className="px-5 py-2">Stav</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(snapshot?.recent || []).length === 0 ? (
                            <tr><td colSpan={6} className="px-5 py-8 text-center text-sm"
                                    style={{ color: 'var(--text-disabled)' }}>
                                Žádná data
                            </td></tr>
                        ) : (snapshot.recent || []).map((c) => (
                            <tr key={c.id} className="border-t" style={{ borderColor: 'var(--border-default)' }}>
                                <td className="px-5 py-3 font-mono text-xs">{fmtTime(c.started_at)}</td>
                                <td className="px-5 py-3 font-mono">{c.caller_id || '—'}</td>
                                <td className="px-5 py-3 font-mono">{c.did_number || '—'}</td>
                                <td className="px-5 py-3 font-mono">{fmtDur(c.duration_seconds)}</td>
                                <td className="px-5 py-3 text-xs">{c.ai_intent || '—'}</td>
                                <td className="px-5 py-3"><Pill status={c.status} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RealtimeMonitor;
